import { ReactNode, useState } from "react";
import { Trash2 } from "lucide-react";
import { UUID } from "@bitory-gmbh/nidavellir";
import { toast } from "sonner";
import {
  joinFieldName,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useHtzSummaryRemoveItemAndDeleteReportMutation } from "@/services/backend/htz/summary/api/summary-api";

/**
 * SummaryItemReportDeleteDialog removes an item from the summary and deletes
 * the Anlagenbericht it summarizes. The backend refuses festgeschriebene
 * reports and reports still used by another summary; nothing is changed then.
 *
 * The dialog is uncontrolled by default. Pass `open` and `onOpenChange` to
 * control it, e.g. when it is opened from a dropdown menu whose content
 * unmounts on close; no trigger is rendered then.
 */
export function SummaryItemReportDeleteDialog({
  summaryId,
  itemId,
  trigger,
  open: controlledOpen,
  onOpenChange,
  onDeleted,
}: {
  summaryId: UUID;
  itemId: UUID;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onDeleted?: () => void;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  const [doDelete, { isLoading, error, reset }] =
    useHtzSummaryRemoveItemAndDeleteReportMutation();
  const { resetFields } = useErrValidationFailedContext();

  const onConfirm = () => {
    if (isLoading) {
      return;
    }

    doDelete({ id: summaryId, itemId })
      .unwrap()
      .then(() => {
        toast.success(t("Anlagenbericht gelöscht"));
        resetFields(joinFieldName("items", itemId));
        reset();
        setOpen(false);
        onDeleted?.();
      })
      .catch(() => {
        // error is surfaced via RTKQueryErrorAlert; swallow the rejection
      });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled &&
        (trigger || (
          <CommonDialogTrigger icon={Trash2} label="" variant="destructive" />
        ))}
      <DialogContent>
        <CommonDialogHeader
          title={t("Anlagenbericht wirklich löschen?")}
          description={t(
            "Die Zeile wird aus dem Inspektionsbericht entfernt und der Anlagenbericht mit allen Beurteilungen und Fotos endgültig gelöscht. Festgeschriebene oder in anderen Inspektionsberichten verwendete Anlagenberichte können nicht gelöscht werden.",
          )}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Löschen")}
          destructive
        />
      </DialogContent>
    </Dialog>
  );
}
